import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import EmployeeLookup from "@/components/EmployeeLookup";
import LetterRichEditor from "@/components/LetterRichEditor";
import LetterBodyView from "@/components/LetterBodyView";
import LetterFormatPanel, {
  DEFAULT_LETTERHEAD,
  resolveLetterBodyFontCss,
} from "@/components/LetterFormatPanel";
import { letterHtmlIsEmpty, plainToLetterHtml } from "@/lib/letterHtml";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  FileSignature,
  Save,
  RotateCcw,
  FileDown,
  Tag,
  Copy,
  Check,
  Loader2,
  AlertCircle,
  Plus,
  Trash2,
  Type,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  CircleCheck,
} from "lucide-react";

const PLACEHOLDER_GROUPS = [
  {
    id: "employee",
    label: "Employee",
    keys: [
      "employee_name", "first_name", "middle_name", "last_name", "full_name", "salutation",
      "employee_code", "employee_address", "address_line1", "address_line2",
      "designation", "department", "work_location", "employment_type", "status",
      "manager_name", "reporting_manager",
      "new_designation", "new_department", "new_work_location",
      "email", "phone",
    ],
  },
  {
    id: "company",
    label: "Company",
    keys: [
      "company_name", "company_legal_name", "company_address", "company_phone",
      "company_email", "company_gstin", "company_cin", "company_pan",
    ],
  },
  {
    id: "dates",
    label: "Dates",
    keys: [
      "today", "today_long", "joining_date", "joining_date_long",
      "last_working_date", "last_working_date_long", "internship_end_date", "resignation_date",
      "confirmation_date", "probation_extension_date", "salary_effective_date",
      "transfer_effective_date", "incident_date", "payment_date",
    ],
  },
  {
    id: "compensation",
    label: "Compensation",
    keys: [
      "ctc", "ctc_inr", "previous_ctc", "settlement_amount",
      "contract_amount", "commission_amount", "reimbursement_amount",
      "other_payments", "deduction_amount", "net_settlement_amount",
      "probation_period", "notice_period",
      "extended_probation_period", "bank_name", "account_number", "ifsc",
    ],
  },
  {
    id: "letter",
    label: "Letter meta",
    keys: [
      "letter_number", "letter_title", "letter_type",
      "reason", "allegation", "response_days", "award_name",
    ],
  },
  {
    id: "signatory",
    label: "Signatory",
    // Name + designation come from Settings → Document Approvals (not per-template fields).
    keys: [
      "signature", "stamp", "authorized_signatory",
    ],
  },
];

const inputCls =
  "w-full border border-slate-200 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30 focus:border-[#226DB4] bg-white";

/** Group letter types for picker (avoids a long scrolling button list). */
const TEMPLATE_GROUPS = [
  {
    id: "joining",
    label: "Joining & offers",
    types: [
      "Internship Offer Letter",
      "Offer Letter",
      "Appointment Letter",
      "Joining Letter",
      "Internship Completion Certificate",
    ],
  },
  {
    id: "career",
    label: "Career & pay",
    types: [
      "Confirmation Letter",
      "Probation Extension Letter",
      "Promotion Letter",
      "Compensation Revision Letter",
      "Transfer Letter",
      "Appraisal Letter",
    ],
  },
  {
    id: "certificates",
    label: "Certificates",
    types: [
      "Salary Certificate",
      "Employment Verification Letter",
      "No Objection Certificate",
      "Character Certificate",
      "Service Certificate",
      "Experience Letter",
      "Appreciation Certificate",
      "Award Letter",
    ],
  },
  {
    id: "exit",
    label: "Exit",
    types: [
      "Resignation Acceptance Letter",
      "Relieving Letter",
      "Full & Final Settlement",
    ],
  },
  {
    id: "contract",
    label: "Contract",
    types: [
      "Contract Offer Letter",
      "Contract Appointment Letter",
      "Contract Completion Letter",
      "Contract Service Certificate",
      "No Dues Certificate",
      "Contract Final Settlement Letter",
      "Contract Letter of Appreciation",
    ],
  },
  {
    id: "disciplinary",
    label: "Disciplinary",
    types: ["Warning Letter", "Show Cause Notice"],
  },
];

function groupIdForType(type, isCustom) {
  if (isCustom) return "custom";
  for (const g of TEMPLATE_GROUPS) {
    if (g.types.includes(type)) return g.id;
  }
  return "custom";
}

function bodyBaseStyle(lh) {
  const f = lh || DEFAULT_LETTERHEAD;
  return {
    fontFamily: resolveLetterBodyFontCss(f.body_font),
    fontSize: `${f.body_font_size || 11}pt`,
    color: f.body_color || "#0f172a",
    lineHeight: 1.5,
  };
}

export default function LetterTemplates() {
  const richRef = useRef(null);
  const [items, setItems] = useState([]); // [{type, body, updated_at, updated_by, is_default, is_custom}]
  const [placeholders, setPlaceholders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [activeType, setActiveType] = useState(null);
  const [editor, setEditor] = useState("");
  const [originalBody, setOriginalBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [copied, setCopied] = useState("");
  const [previewEmp, setPreviewEmp] = useState("");
  const [livePreview, setLivePreview] = useState(null); // { body, checklist, letter_number }
  const [previewing, setPreviewing] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [phSearch, setPhSearch] = useState("");
  const [openPhGroups, setOpenPhGroups] = useState(() =>
    Object.fromEntries(
      PLACEHOLDER_GROUPS.map((g) => [g.id, g.id === "employee" || g.id === "letter" || g.id === "signatory"]),
    ),
  );
  const [requiresSignature, setRequiresSignature] = useState(true);
  const [requiresStamp, setRequiresStamp] = useState(false);
  const [originalRequiresSignature, setOriginalRequiresSignature] = useState(true);
  const [originalRequiresStamp, setOriginalRequiresStamp] = useState(false);
  const [groupFilter, setGroupFilter] = useState("joining");
  const [createOpen, setCreateOpen] = useState(false);
  const [newTypeName, setNewTypeName] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [companyLhOpen, setCompanyLhOpen] = useState(false);
  const [companyLetterhead, setCompanyLetterhead] = useState(DEFAULT_LETTERHEAD);
  const [lhSaving, setLhSaving] = useState(false);
  const [companyFormatTab, setCompanyFormatTab] = useState("header");
  const previewSeq = useRef(0);

  const loadAll = async () => {
    const [tpls, phs, emps, lh] = await Promise.all([
      api.get("/letter-templates", { params: { with_body: true } }),
      api.get("/letter-templates/placeholders"),
      api.get("/employees").catch(() => ({ data: [] })),
      api.get("/letter-letterhead").catch(() => ({ data: DEFAULT_LETTERHEAD })),
    ]);
    const companyLh = { ...DEFAULT_LETTERHEAD, ...(lh.data || {}) };
    setItems(tpls.data || []);
    setPlaceholders(phs.data || []);
    setEmployees(emps.data || []);
    setCompanyLetterhead(companyLh);
    if (!activeType && (tpls.data || []).length) {
      const first = tpls.data[0];
      const html = plainToLetterHtml(first.body);
      const reqSig = first.requires_signature !== undefined && first.requires_signature !== null
        ? !!first.requires_signature
        : true;
      const reqStamp = first.requires_stamp !== undefined && first.requires_stamp !== null
        ? !!first.requires_stamp
        : false;
      setActiveType(first.type);
      setEditor(html);
      setOriginalBody(html);
      setRequiresSignature(reqSig);
      setRequiresStamp(reqStamp);
      setOriginalRequiresSignature(reqSig);
      setOriginalRequiresStamp(reqStamp);
      setGroupFilter(groupIdForType(first.type, first.is_custom));
    }
    if ((emps.data || []).length && !previewEmp) {
      setPreviewEmp(emps.data[0].id);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const active = useMemo(
    () => items.find((x) => x.type === activeType),
    [items, activeType],
  );

  const dirty =
    editor !== originalBody
    || requiresSignature !== originalRequiresSignature
    || requiresStamp !== originalRequiresStamp;

  const groupTabs = useMemo(() => {
    const customCount = items.filter((x) => x.is_custom).length;
    const tabs = TEMPLATE_GROUPS.map((g) => ({
      ...g,
      count: items.filter((x) => !x.is_custom && g.types.includes(x.type)).length,
    }));
    if (customCount > 0) {
      tabs.push({ id: "custom", label: "Custom", types: [], count: customCount });
    }
    return tabs;
  }, [items]);

  const groupItems = useMemo(() => {
    if (groupFilter === "custom") {
      return items.filter((x) => x.is_custom);
    }
    const g = TEMPLATE_GROUPS.find((x) => x.id === groupFilter);
    if (!g) return items;
    const order = new Map(g.types.map((t, i) => [t, i]));
    return items
      .filter((x) => !x.is_custom && g.types.includes(x.type))
      .sort((a, b) => (order.get(a.type) ?? 99) - (order.get(b.type) ?? 99));
  }, [items, groupFilter]);

  const switchTo = (type) => {
    if (type === activeType) return;
    if (dirty && !window.confirm("Discard unsaved changes?")) return;
    const t = items.find((x) => x.type === type);
    if (!t) return;
    const html = plainToLetterHtml(t.body);
    const reqSig = t.requires_signature !== undefined && t.requires_signature !== null
      ? !!t.requires_signature
      : true;
    const reqStamp = t.requires_stamp !== undefined && t.requires_stamp !== null
      ? !!t.requires_stamp
      : false;
    setActiveType(type);
    setEditor(html);
    setOriginalBody(html);
    setRequiresSignature(reqSig);
    setRequiresStamp(reqStamp);
    setOriginalRequiresSignature(reqSig);
    setOriginalRequiresStamp(reqStamp);
    setErr("");
    setOkMsg("");
    setLivePreview(null);
    setGroupFilter(groupIdForType(type, t.is_custom));
  };

  const placeholderByKey = useMemo(() => {
    const m = {};
    placeholders.forEach((p) => { m[p.key] = p; });
    return m;
  }, [placeholders]);

  const groupedPlaceholders = useMemo(() => {
    const q = phSearch.trim().toLowerCase();
    const used = new Set();
    const groups = PLACEHOLDER_GROUPS.map((g) => {
      const items = g.keys
        .map((k) => placeholderByKey[k])
        .filter(Boolean)
        .filter((p) => {
          if (!q) return true;
          return (
            p.key.toLowerCase().includes(q) ||
            (p.label || "").toLowerCase().includes(q) ||
            String(p.example || "").toLowerCase().includes(q)
          );
        });
      items.forEach((p) => used.add(p.key));
      return { ...g, items };
    });
    const other = placeholders.filter((p) => {
      if (used.has(p.key)) return false;
      if (!q) return true;
      return (
        p.key.toLowerCase().includes(q) ||
        (p.label || "").toLowerCase().includes(q)
      );
    });
    if (other.length) {
      groups.push({ id: "other", label: "Other", items: other });
    }
    return groups.filter((g) => g.items.length > 0);
  }, [placeholders, placeholderByKey, phSearch]);

  const refreshLivePreview = async () => {
    if (!activeType || !previewEmp || letterHtmlIsEmpty(editor)) {
      setLivePreview(null);
      return;
    }
    const seq = ++previewSeq.current;
    setPreviewing(true);
    try {
      const r = await api.post("/letter-templates/preview", {
        employee_id: previewEmp,
        type: activeType,
        body: editor,
      });
      if (seq !== previewSeq.current) return;
      setErr("");
      setLivePreview({
        body: r.data.body,
        checklist: r.data.checklist || { used: [], empty: [], important_missing: [] },
        letter_number: r.data.letter_number,
      });
    } catch (ex) {
      if (seq !== previewSeq.current) return;
      const d = ex?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Live preview failed");
    } finally {
      if (seq === previewSeq.current) setPreviewing(false);
    }
  };

  // Debounced live preview as editor / employee / type changes
  useEffect(() => {
    if (!activeType || !previewEmp) return undefined;
    const t = setTimeout(() => {
      refreshLivePreview();
    }, 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, previewEmp, activeType]);

  const downloadPdfPreview = async () => {
    if (!activeType || !previewEmp) return;
    setPdfBusy(true);
    setErr("");
    try {
      const res = await api.post(
        "/letter-templates/preview.pdf",
        { employee_id: previewEmp, type: activeType, body: editor },
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `${String(activeType).replace(/ /g, "_")}_preview.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "PDF preview failed");
    } finally {
      setPdfBusy(false);
    }
  };

  const insertPlaceholder = (key) => {
    const tag = `{{${key}}}`;
    if (richRef.current?.insertText) {
      richRef.current.insertText(tag);
      return;
    }
    setEditor((b) => (b || "") + tag);
  };

  const copyPlaceholder = async (key) => {
    try {
      await navigator.clipboard.writeText(`{{${key}}}`);
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
    } catch (e) {
      // Fallback noop
    }
  };

  const save = async () => {
    if (!activeType) return;
    if (letterHtmlIsEmpty(editor)) {
      setErr("Template body cannot be empty");
      return;
    }
    setSaving(true);
    setErr("");
    setOkMsg("");
    try {
      const payload = {
        body: editor,
        requires_signature: requiresSignature,
        requires_stamp: requiresStamp,
      };
      if (active?.format) payload.format = active.format;
      const r = await api.put(
        `/letter-templates/${encodeURIComponent(activeType)}`,
        payload,
      );
      // Update local cache
      const savedHtml = plainToLetterHtml(r.data.body);
      const savedSig = r.data.requires_signature !== undefined && r.data.requires_signature !== null
        ? !!r.data.requires_signature
        : requiresSignature;
      const savedStamp = r.data.requires_stamp !== undefined && r.data.requires_stamp !== null
        ? !!r.data.requires_stamp
        : requiresStamp;
      setItems((arr) =>
        arr.map((x) =>
          x.type === activeType
            ? {
                ...x,
                body: savedHtml,
                updated_at: r.data.updated_at,
                updated_by: r.data.updated_by,
                is_default: false,
                is_custom: r.data.is_custom ?? x.is_custom,
                requires_signature: savedSig,
                requires_stamp: savedStamp,
              }
            : x,
        ),
      );
      setEditor(savedHtml);
      setOriginalBody(savedHtml);
      setRequiresSignature(savedSig);
      setRequiresStamp(savedStamp);
      setOriginalRequiresSignature(savedSig);
      setOriginalRequiresStamp(savedStamp);
      setOkMsg("Saved successfully");
      setTimeout(() => setOkMsg(""), 3000);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!activeType) return;
    if (active?.is_custom) {
      setErr("Custom types cannot be reset — edit or delete them instead");
      return;
    }
    if (!window.confirm(`Reset "${activeType}" back to the factory default? Your custom edits will be lost.`)) return;
    setResetBusy(true);
    setErr("");
    setOkMsg("");
    try {
      const r = await api.post(`/letter-templates/${encodeURIComponent(activeType)}/reset`);
      const html = plainToLetterHtml(r.data.body);
      setItems((arr) =>
        arr.map((x) =>
          x.type === activeType
            ? {
                ...x,
                body: html,
                updated_at: null,
                updated_by: null,
                is_default: true,
                is_custom: false,
                requires_signature: null,
                requires_stamp: null,
              }
            : x,
        ),
      );
      setEditor(html);
      setOriginalBody(html);
      setRequiresSignature(true);
      setRequiresStamp(false);
      setOriginalRequiresSignature(true);
      setOriginalRequiresStamp(false);
      setOkMsg("Reset to factory default");
      setTimeout(() => setOkMsg(""), 3000);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to reset");
    } finally {
      setResetBusy(false);
    }
  };

  const createTemplate = async () => {
    const name = newTypeName.trim();
    if (!name) {
      setErr("Enter a template name");
      return;
    }
    setCreating(true);
    setErr("");
    try {
      const r = await api.post("/letter-templates", { type: name });
      const html = plainToLetterHtml(r.data.body);
      const created = {
        ...r.data,
        body: html,
        requires_signature: r.data.requires_signature ?? true,
        requires_stamp: r.data.requires_stamp ?? false,
      };
      setItems((arr) => [...arr, created]);
      setCreateOpen(false);
      setNewTypeName("");
      setActiveType(created.type);
      setEditor(html);
      setOriginalBody(html);
      setRequiresSignature(!!created.requires_signature);
      setRequiresStamp(!!created.requires_stamp);
      setOriginalRequiresSignature(!!created.requires_signature);
      setOriginalRequiresStamp(!!created.requires_stamp);
      setGroupFilter("custom");
      setOkMsg(`Created “${r.data.type}”`);
      setTimeout(() => setOkMsg(""), 3000);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to create template");
    } finally {
      setCreating(false);
    }
  };

  const deleteTemplate = async () => {
    if (!activeType || !active?.is_custom) return;
    if (!window.confirm(`Delete custom template “${activeType}”? This cannot be undone.`)) return;
    setDeleting(true);
    setErr("");
    try {
      await api.delete(`/letter-templates/${encodeURIComponent(activeType)}`);
      const next = items.filter((x) => x.type !== activeType);
      setItems(next);
      const first = next[0];
      const html = first ? plainToLetterHtml(first.body) : "";
      setActiveType(first?.type || null);
      setEditor(html);
      setOriginalBody(html);
      if (first) setGroupFilter(groupIdForType(first.type, first.is_custom));
      setOkMsg("Template deleted");
      setTimeout(() => setOkMsg(""), 3000);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  const saveCompanyLetterhead = async () => {
    setLhSaving(true);
    setErr("");
    try {
      const r = await api.put("/letter-letterhead", companyLetterhead);
      const saved = { ...DEFAULT_LETTERHEAD, ...(r.data || {}) };
      setCompanyLetterhead(saved);
      setCompanyLhOpen(false);
      setOkMsg("Letter format saved — applies to new PDF downloads");
      setTimeout(() => setOkMsg(""), 4000);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Failed to save company letterhead");
    } finally {
      setLhSaving(false);
    }
  };

  return (
    <AdminLayout
      title="Letter Templates"
      breadcrumbs={["Home", "Organization", "Letter Templates"]}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-slate-600 flex items-center gap-2">
          <FileSignature className="h-4 w-4 text-[#226DB4]" />
          Choose a category, then pick the letter template to edit.
        </div>
        <button
          type="button"
          data-testid="letterhead-settings-btn"
          onClick={() => setCompanyLhOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-200 rounded bg-white hover:bg-slate-50 text-slate-700 font-medium"
          title="One-time header, footer and letter text formatting"
        >
          <Type className="h-3.5 w-3.5 text-[#226DB4]" />
          Letter format
        </button>
      </div>

      {/* Category + letter on left; placeholders always visible on right */}
      <div className="bg-white border border-slate-200 rounded shadow-sm mb-3 overflow-hidden" data-testid="template-picker">
        <div className="flex flex-col lg:flex-row lg:items-stretch">
          <div className="flex-1 min-w-0 px-3 py-2.5">
            <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              Letter category
            </div>
            <div className="flex flex-wrap gap-1 mb-2.5" data-testid="template-group-tabs">
              {groupTabs.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  data-testid={`template-group-${g.id}`}
                  onClick={() => {
                    setGroupFilter(g.id);
                    const list =
                      g.id === "custom"
                        ? items.filter((x) => x.is_custom)
                        : items.filter((x) => !x.is_custom && (g.types || []).includes(x.type));
                    if (list.length && !list.some((x) => x.type === activeType)) {
                      switchTo(list[0].type);
                    }
                  }}
                  className={`px-2 py-1 text-[11px] font-medium rounded border transition ${
                    groupFilter === g.id
                      ? "bg-[#226DB4] border-[#226DB4] text-white"
                      : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {g.label}
                  <span className={`ml-1 ${groupFilter === g.id ? "text-sky-100" : "text-slate-400"}`}>{g.count}</span>
                </button>
              ))}
            </div>
            <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              Letter template
            </div>
            <div className="flex items-center gap-2 max-w-md">
              <select
                id="letter-template-select"
                data-testid="template-select"
                className="border border-slate-200 rounded px-2 py-1.5 text-xs bg-white flex-1 min-w-0 focus:outline-none focus:ring-2 focus:ring-[#226DB4]/30"
                value={activeType || ""}
                onChange={(e) => switchTo(e.target.value)}
              >
                {groupItems.length === 0 && <option value="">No templates</option>}
                {groupItems.map((t) => (
                  <option key={t.type} value={t.type}>
                    {t.type}
                    {t.is_custom ? " (Custom)" : !t.is_default ? " (Edited)" : ""}
                  </option>
                ))}
              </select>
              <button
                type="button"
                data-testid="new-template-btn"
                onClick={() => { setCreateOpen(true); setErr(""); }}
                className="inline-flex items-center gap-1 px-2 py-1.5 text-[11px] bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium shrink-0"
              >
                <Plus className="h-3 w-3" /> New
              </button>
            </div>
          </div>

          <div
            className="lg:w-[22rem] xl:w-96 shrink-0 border-t lg:border-t-0 lg:border-l border-slate-200 bg-slate-50/50"
            data-testid="placeholders-top"
          >
            <div className="px-3 py-1.5 border-b border-slate-200 flex items-center justify-between gap-2">
              <div className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
                <Tag className="h-3.5 w-3.5 text-[#226DB4]" />
                Placeholders
                <span className="normal-case font-normal text-slate-400 text-[10px]">click to insert</span>
              </div>
              {livePreview?.checklist?.important_missing?.length > 0 ? (
                <span className="text-[10px] text-amber-700 flex items-center gap-1" data-testid="placeholder-checklist">
                  <CircleAlert className="h-3 w-3" />
                  {livePreview.checklist.important_missing.length} empty
                </span>
              ) : livePreview?.checklist ? (
                <span className="text-[10px] text-emerald-700 flex items-center gap-1" data-testid="placeholder-checklist">
                  <CircleCheck className="h-3 w-3" /> OK
                </span>
              ) : null}
            </div>
            <div className="px-2.5 py-1.5 border-b border-slate-100">
              <input
                data-testid="placeholder-search"
                value={phSearch}
                onChange={(e) => setPhSearch(e.target.value)}
                placeholder="Search fields…"
                className="w-full border border-slate-200 rounded px-2 py-1 text-xs bg-white"
              />
            </div>
            <div className="max-h-[148px] overflow-y-auto" data-testid="placeholder-list">
              {groupedPlaceholders.map((g) => {
                const open = openPhGroups[g.id] !== false;
                return (
                  <div key={g.id} className="border-b border-slate-100 last:border-b-0">
                    <button
                      type="button"
                      className="w-full flex items-center justify-between px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-white/80"
                      onClick={() => setOpenPhGroups((s) => ({ ...s, [g.id]: !open }))}
                    >
                      <span>{g.label}</span>
                      <span className="flex items-center gap-1 text-slate-400 font-normal">
                        {g.items.length}
                        {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                      </span>
                    </button>
                    {open && (
                      <ul>
                        {g.items.map((p) => (
                          <li key={p.key} className="px-2.5 py-1 hover:bg-white border-t border-slate-50/80">
                            <div className="flex items-center justify-between gap-1">
                              <button
                                data-testid={`insert-placeholder-${p.key}`}
                                onClick={() => insertPlaceholder(p.key)}
                                className="text-left flex-1 min-w-0"
                                title={p.label}
                              >
                                <code className="text-[11px] text-[#226DB4] font-mono truncate block">
                                  {`{{${p.key}}}`}
                                </code>
                                <span className="text-[10px] text-slate-500 truncate block">{p.label}</span>
                              </button>
                              <button
                                onClick={() => copyPlaceholder(p.key)}
                                className="p-1 text-slate-400 hover:text-slate-700 rounded shrink-0"
                                title="Copy"
                              >
                                {copied === p.key ? (
                                  <Check className="h-3 w-3 text-emerald-600" />
                                ) : (
                                  <Copy className="h-3 w-3" />
                                )}
                              </button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
              {groupedPlaceholders.length === 0 && (
                <div className="px-2 py-3 text-center text-slate-400 text-xs">No matches</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {err && (
        <div className="mb-3 bg-rose-50 border border-rose-200 text-rose-700 px-3 py-2 rounded text-sm flex items-start gap-2" data-testid="editor-error">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> {err}
        </div>
      )}
      {okMsg && (
        <div className="mb-3 bg-emerald-50 border border-emerald-200 text-emerald-700 px-3 py-2 rounded text-sm" data-testid="editor-ok">
          {okMsg}
        </div>
      )}

      {/* Main workspace — most of the viewport */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 min-h-[calc(100vh-220px)]">
        <div className="bg-white border border-slate-200 rounded shadow-sm min-w-0 flex flex-col">
          <div className="px-4 py-2.5 border-b border-slate-200 flex flex-wrap items-center justify-between gap-2 shrink-0">
            <div>
              <div className="text-sm font-semibold text-slate-800" data-testid="editor-template-name">
                {activeType || "—"}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {active?.is_default ? (
                  <span className="text-slate-500">Factory default — not yet customised</span>
                ) : active?.updated_at ? (
                  <span>
                    Last edited by{" "}
                    <span className="font-medium text-slate-700">{active.updated_by || "—"}</span>{" "}
                    on {formatDisplayDate(active.updated_at)}
                  </span>
                ) : null}
                {dirty && <span className="ml-2 text-amber-700">· Unsaved</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {active?.is_custom ? (
                <button
                  data-testid="delete-template-btn"
                  onClick={deleteTemplate}
                  disabled={deleting}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-rose-200 rounded hover:bg-rose-50 text-rose-700 disabled:opacity-50"
                >
                  {deleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                  Delete
                </button>
              ) : (
                <button
                  data-testid="reset-template-btn"
                  onClick={resetToDefault}
                  disabled={resetBusy || !active || active.is_default}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-slate-200 rounded hover:bg-slate-50 text-slate-700 disabled:opacity-50"
                  title={active?.is_default ? "Already on default" : "Reset to factory default"}
                >
                  {resetBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                  Reset
                </button>
              )}
              <button
                data-testid="save-template-btn"
                onClick={save}
                disabled={saving || !dirty}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs bg-[#089949] hover:bg-[#067A3A] text-white rounded font-medium disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save
              </button>
            </div>
          </div>
          <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/80 shrink-0">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
              <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="requires-signature-checkbox"
                  checked={requiresSignature}
                  onChange={(e) => setRequiresSignature(e.target.checked)}
                  className="rounded border-slate-300 text-[#226DB4] focus:ring-[#226DB4]"
                />
                Requires signature on Issue
              </label>
              <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="requires-stamp-checkbox"
                  checked={requiresStamp}
                  onChange={(e) => setRequiresStamp(e.target.checked)}
                  className="rounded border-slate-300 text-[#226DB4] focus:ring-[#226DB4]"
                />
                Requires stamp on Issue
              </label>
            </div>
            <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">
              Insert {"{{signature}}"} or {"{{authorized_signatory}}"} where the signature should appear.
              If missing, signature is added at the end. Signatory name and designation come from
              Settings → Document Approvals (not template placeholders).
            </p>
          </div>
          <div className="p-3 flex-1 min-h-0">
            <LetterRichEditor
              ref={richRef}
              value={editor}
              onChange={setEditor}
              minHeight={560}
              testid="template-rich-editor"
              placeholder="Write your letter… Click a placeholder on the right to insert"
              baseStyle={bodyBaseStyle(companyLetterhead)}
            />
          </div>
        </div>

        <div className="bg-slate-100 border border-slate-200 rounded shadow-sm min-w-0 flex flex-col" data-testid="live-preview-panel">
          <div className="px-3 py-2.5 border-b border-slate-200 bg-white rounded-t flex flex-wrap items-center justify-between gap-2 shrink-0">
            <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
              Live preview
              {previewing && <Loader2 className="inline h-3 w-3 ml-1.5 animate-spin text-slate-400" />}
            </div>
            <button
              type="button"
              data-testid="pdf-preview-btn"
              onClick={downloadPdfPreview}
              disabled={pdfBusy || !previewEmp || !activeType}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium disabled:opacity-50"
            >
              {pdfBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileDown className="h-3 w-3" />}
              PDF preview
            </button>
          </div>
          <div className="px-3 py-2 bg-white border-b border-slate-200 shrink-0">
            <EmployeeLookup
              employees={employees}
              value={previewEmp}
              onChange={setPreviewEmp}
              testid="preview-employee-lookup"
            />
            {livePreview?.letter_number && (
              <div className="text-[10px] text-slate-500 mt-1.5 font-mono">
                Sample Ref: {livePreview.letter_number}
                {dirty ? " · draft body" : ""}
              </div>
            )}
          </div>
          <div className="p-4 flex-1 overflow-y-auto min-h-0">
            <div
              className="mx-auto bg-white shadow-md border border-slate-200 px-7 pt-8 pb-4 min-h-[520px] flex flex-col"
              style={{ maxWidth: 560 }}
              data-testid="preview-output"
            >
              <div className="flex-1">
                {livePreview?.body ? (
                  <LetterBodyView
                    body={livePreview.body}
                    baseStyle={{
                      ...bodyBaseStyle(companyLetterhead),
                      lineHeight: 1.5,
                    }}
                  />
                ) : (
                  <div className="text-xs text-slate-400 text-center py-20">
                    {previewEmp ? "Rendering preview…" : "Select an employee to preview"}
                  </div>
                )}
              </div>
              {livePreview?.body ? (
                <p className="mt-8 pt-3 border-t border-slate-100 text-[9px] text-slate-500 text-center italic">
                  This is a system-generated document and does not require a physical or digital signature.
                </p>
              ) : null}
            </div>
            <p className="text-[10px] text-slate-500 mt-2 text-center">
              HTML preview · PDF places the disclaimer at the bottom of the last page
            </p>
          </div>
        </div>
      </div>

      {createOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-800">New custom letter type</h3>
              <button type="button" onClick={() => setCreateOpen(false)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded">✕</button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1.5">Template name</label>
                <input
                  data-testid="new-template-name"
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                  placeholder="e.g. Bond Completion Letter"
                  className={inputCls}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      createTemplate();
                    }
                  }}
                />
              </div>
              <p className="text-xs text-slate-500">
                Creates a new letter type you can edit and use when issuing letters. System types stay listed separately.
              </p>
            </div>
            <div className="px-5 py-3.5 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
              <button type="button" onClick={() => setCreateOpen(false)} className="px-3 py-1.5 text-sm border border-slate-200 rounded bg-white">
                Cancel
              </button>
              <button
                type="button"
                data-testid="create-template-confirm"
                onClick={createTemplate}
                disabled={creating || !newTypeName.trim()}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded font-medium disabled:opacity-60"
              >
                {creating && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {companyLhOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col" data-testid="letterhead-modal">
            <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-800">Letter format</h3>
              <button type="button" onClick={() => setCompanyLhOpen(false)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3 text-sm">
              <p className="text-xs text-slate-500">
                One-time settings for PDF header, footer and default letter text. Applies to all letter types.
              </p>
              <LetterFormatPanel
                letterhead={companyLetterhead}
                onLetterheadChange={setCompanyLetterhead}
                bodyFormat={companyLetterhead}
                onBodyChange={(patch) => setCompanyLetterhead((prev) => ({ ...prev, ...patch }))}
                tab={companyFormatTab}
                onTabChange={setCompanyFormatTab}
                showBody={true}
              />
            </div>
            <div className="px-5 py-3.5 border-t border-slate-200 flex justify-end gap-2 bg-slate-50">
              <button type="button" onClick={() => setCompanyLhOpen(false)} className="px-3 py-1.5 text-sm border border-slate-200 rounded bg-white">
                Cancel
              </button>
              <button
                type="button"
                data-testid="letterhead-save-btn"
                onClick={saveCompanyLetterhead}
                disabled={lhSaving}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm bg-[#089949] hover:bg-[#067A3A] text-white rounded font-medium disabled:opacity-60"
              >
                {lhSaving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
